import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  PlusIcon,
  Eye,
  Edit,
  User,
  Crown,
  MapPin,
  Phone,
  Calendar,
  Search,
} from "lucide-react";

import { Button } from "@shared/ui/button";
import { Switch } from "@shared/ui/switch";
import { useToast } from "@shared/hooks/use-toast";
import {
  FieldTooltip,
  Tooltip,
  TooltipProvider,
  TooltipTrigger,
} from "@shared/ui/tooltip";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { FilterConfig, FilterValues } from "@shared/types/tableFilters";
import { PAGE_SIZE } from "@shared/utils/constants";
import FieldForceDashboard from "../fieldForceDashboard/fieldForceDashboard";
import {
  useFieldForceExport,
  useGetFieldForceData,
  useUpdateFieldForceStatus,
} from "../../hooks";
import FieldForceAddModal from "./FieldForceAddModal/fieldForceAddModal";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { Input } from "@shared/ui/input";
import { usePageViewTimer } from "@shared/hooks/usePageViewTimer";

// Updated AddTechnicianButton to accept onClick prop
const AddTechnicianButton = React.forwardRef<
  HTMLButtonElement,
  { onClick?: () => void }
>(({ onClick, ...props }, ref) => {
  return (
    <Button
      ref={ref}
      onClick={onClick}
      className="bg-blue-600 hover:bg-blue-700"
      {...props}
    >
      <PlusIcon className="h-4 w-4 mr-2" />
      Add Technician
    </Button>
  );
});

AddTechnicianButton.displayName = "AddTechnicianButton";

interface FieldForceData {
  id: string;
  fieldForceNo: string;
  ffId: string;
  name: string;
  contact: string;
  role: "Technician" | "Supervisor";
  zone: string;
  area: string;
  joiningDate: string;
  status: "Active" | "Inactive";
  assignedSO: number;
  completedSO: number;
  isActive: boolean;
}

const FieldForceLanding: React.FC = () => {
  usePageViewTimer({ pageName: "WX Field Force" });
  const navigate = useNavigate();
  const remoteUtilityId = useRemoteUtilityId();
  const updateStatus = useUpdateFieldForceStatus();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(PAGE_SIZE);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [filters, setFilters] = useState<FilterValues>({});

  const { data: getItems, refetch } = useGetFieldForceData(
    1,
    PAGE_SIZE,
    searchQuery
  );

  const { exportCSV, isExporting } = useFieldForceExport(
    remoteUtilityId,
    searchQuery
  );

  const handleExport = () => {
    exportCSV();
  };

  useEffect(() => {
    refetch(); // Just refetch the current query
  }, [location.pathname]);
  // Add state for modal
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: PAGE_SIZE,
    startItem: 0,
    endItem: 0,
  });

  useEffect(() => {
    if (getItems) {
      const totalItems = getItems.count || 0;
      const totalPages = Math.ceil(totalItems / itemsPerPage);
      const startItem = (currentPage - 1) * itemsPerPage + 1;
      const endItem = Math.min(currentPage * itemsPerPage, totalItems);

      setPagination({
        currentPage: getItems.page_number || currentPage,
        totalPages,
        totalItems,
        itemsPerPage,
        startItem: totalItems > 0 ? startItem : 0,
        endItem: totalItems > 0 ? endItem : 0,
      });
    }
  }, [getItems, currentPage, itemsPerPage]);

  const handlePageChange = async (page: number) => {
    if (page !== currentPage && page >= 1 && page <= pagination.totalPages) {
      setCurrentPage(page);
    }
  };

  // Handle Add Technician button click
  const handleAddTechnicianClick = () => {
    setIsModalOpen(true);
  };

  // Handle modal redirect
  const handleModalRedirect = () => {
    setIsModalOpen(false);
    navigate("/utility/utility-setup/user-management");
  };

  const filterConfig: FilterConfig[] = [
    {
      key: "search",
      label: "Search",
      type: "text",
      placeholder: "Search personnel...",
    },
    {
      key: "role",
      label: "Role",
      type: "select",
      options: [
        { label: "Technician", value: "technician" },
        { label: "Supervisor", value: "supervisor" },
      ],
    },
    {
      key: "zone",
      label: "Zone",
      type: "select",
      options: [
        { label: "North Zone", value: "north" },
        { label: "South Zone", value: "south" },
        { label: "East Zone", value: "east" },
        { label: "West Zone", value: "west" },
      ],
    },
    {
      key: "status",
      label: "Status",
      type: "select",
      options: [
        { label: "Active", value: "active" },
        { label: "Inactive", value: "inactive" },
      ],
    },
  ];

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
  };

  const handleFilterChange = async (key: string, value: any) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handleFilterClear = () => {
    setFilters({});
    setCurrentPage(1);
  };

  const handleToggleStatus = async (row: FieldForceData, checked: boolean) => {
    const payload = {
      id: row.id,
      is_active: checked,
      remote_utility_id: remoteUtilityId,
    };
    updateStatus.mutateAsync(payload);
  };

  const columns: TableColumn<FieldForceData>[] = [
    {
      key: "fieldForceNo",
      header: "FF ID",
      sortable: true,
      width: "w-20",
    },
    {
      key: "name",
      header: "Name",
      sortable: true,
      render: (value: string, row: FieldForceData) => (
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
            <span className="text-sm font-medium text-blue-600">
              {value
                .split(" ")
                .map((n) => n[0])
                .join("")
                .toUpperCase()}
            </span>
          </div>
          <span className="font-medium">{value}</span>
        </div>
      ),
    },
    {
      key: "contactNumber",
      header: "Contact",
      sortable: true,
      render: (value: string) => (
        <div className="flex items-center gap-2">
          <Phone className="h-4 w-4 text-gray-400" />
          <span>{value}</span>
        </div>
      ),
    },
    {
      key: "roleName",
      header: "Role",
      sortable: true,
      render: (value: string) => (
        <div className="flex items-center gap-2">
          {value === "Technician" && (
            <User className="h-4 w-4 text-orange-500" />
          )}
          {value === "Supervisor" && (
            <Crown className="h-4 w-4 text-purple-500" />
          )}
          <span>{value}</span>
        </div>
      ),
    },
    {
      key: "areaNames",
      header: "Area",
      sortable: true,
      render: (value: string[]) => (
        <div className="flex items-center gap-2">
          <span>{value.map((area: string) => area).join(", ") || "N/A"}</span>
        </div>
      ),
    },
    {
      key: "subAreaNames",
      header: "Sub Area",
      sortable: true,
      render: (value: string[]) => (
        <div className="flex items-center gap-2">
          <span>{value.map((subArea: string) => subArea).join(", ") || "N/A"}</span>
        </div>
      ),
    },
    {
      key: "joiningDate",
      header: "Joining Date",
      sortable: true,
    },
    {
      key: "total",
      header: "Assigned SO",
      sortable: true,
      width: "w-24",
    },
    {
      key: "completed",
      header: "Completed SO",
      sortable: true,
      width: "w-24",
    },
    {
      key: "isActive",
      header: "Status",
      sortable: true,
      render: (value: boolean) => (
        <span
          className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
            value ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
          }`}
        >
          {value ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      sortable: false,
      width: "w-32",
      render: (value: boolean, row: FieldForceData) => (
        <div className="flex items-center gap-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() =>
                    navigate(
                      `/service-orders/field-force/view/${row.id}/${row.fieldForceNo}/`
                    )
                  }
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <Eye className="h-4 w-4 text-gray-500" />
                </button>
              </TooltipTrigger>
              <FieldTooltip>View Details</FieldTooltip>
            </Tooltip>
          </TooltipProvider>

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() =>
                    navigate(
                      `/service-orders/field-force/edit/${row.id}/${row.fieldForceNo}`
                    )
                  }
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <Edit className="h-4 w-4 text-gray-500" />
                </button>
              </TooltipTrigger>
              <FieldTooltip>Edit</FieldTooltip>
            </Tooltip>
          </TooltipProvider>

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-2">
                  <Switch
                    id={`technician-${row.id}`}
                    checked={row.isActive}
                    onCheckedChange={(checked) =>
                      handleToggleStatus(row, checked)
                    }
                  />
                </div>
              </TooltipTrigger>
              <FieldTooltip>
                {row.isActive ? "Inactive" : "Active"} Technician
              </FieldTooltip>
            </Tooltip>
          </TooltipProvider>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in mt-6 p-2">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="page-title">
            Field Force Management
          </h2>
          <p className="page-subtitle">
            Manage your technicians, supervisors, and field personnel
          </p>
        </div>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <AddTechnicianButton onClick={handleAddTechnicianClick} />
            </TooltipTrigger>
            <FieldTooltip>
              Add a new technician or supervisor to your field force
            </FieldTooltip>
          </Tooltip>
        </TooltipProvider>
      </div>

      {/* Dashboard Cards */}
      <FieldForceDashboard />

      {/* Data Table */}
      <div className="mt-2">
        <GenericTable
          data={getItems?.results}
          columns={columns}
          rowKey="id"
          selectable={false}
          emptyMessage="No field force personnel found"
          pagination={pagination}
          onSearch={handleSearchChange}
          searchParamName="query"
          searchDebounceMs={300}
          searchPlaceholder="Search field force by name..."
          onPageChange={handlePageChange}
          onExport={handleExport}
          exportLoading={isExporting}
        />
      </div>

      {/* Add Technician Modal */}
      <FieldForceAddModal
        isOpen={isModalOpen}
        onOpenChange={setIsModalOpen}
        onRedirect={handleModalRedirect}
      />
    </div>
  );
};

export default FieldForceLanding;
